import { Navigate, Outlet } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { isWorkspaceEmail } from '@/auth/workspace-domain';
import { TOOLS_GATE } from './access';
import { type ToolsPermissionsState, useToolsPermissions } from './use-tools-permissions';

/**
 * The route half of `TOOLS_GATE` (the rail reads the same constant from the module catalog). A
 * caller the rule does not admit goes to the dashboard; while the rule is still being read nothing
 * renders, so a reload does not bounce someone entitled. Convenience only: the server's guards
 * decide.
 */
export function ToolsGate() {
  const { user } = useAuth();
  const permissions = useToolsPermissions();
  const open = isOpen(isWorkspaceEmail(user?.email), permissions);
  if (open === 'unknown') return null;
  return open ? <Outlet /> : <Navigate to="/" replace />;
}

/**
 * Every gate kind is named, so the compiler points here whenever `TOOLS_GATE` changes kind. The
 * ones Strumenti does not use close the route.
 */
function isOpen(
  isWorkspaceAccount: boolean,
  permissions: ToolsPermissionsState,
): boolean | 'unknown' {
  const gate = TOOLS_GATE;
  switch (gate.kind) {
    case 'workspace-domain':
      return isWorkspaceAccount;
    case 'always':
      return true;
    case 'permission':
      if (permissions.status === 'loading') return 'unknown';
      // Fails closed: an error holds no permission.
      return (
        permissions.status === 'ready' &&
        gate.anyOf.some((code) => permissions.permissions.includes(code))
      );
    case 'finance-capability':
    case 'operator':
    case 'administrator':
    case 'registry':
      return false;
    default: {
      const unhandled: never = gate;
      return unhandled;
    }
  }
}
